import { useQuery } from '@tanstack/react-query';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import ReactECharts from 'echarts-for-react';
import {
  CircleStackIcon,
  ServerStackIcon,
  TableCellsIcon,
  CubeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

// Color palette for charts
const COLORS = {
  'Raw Data': '#3b82f6',      // Blue
  'Marts': '#10b981',         // Emerald
  'Wide Views': '#8b5cf6',    // Purple
  'Staging': '#f59e0b'        // Amber
};

const TYPE_ICONS = {
  'Raw Data': ServerStackIcon,
  'Marts': CubeIcon,
  'Wide Views': TableCellsIcon,
  'Staging': ArrowPathIcon
};

function StorageCard({ title, value, subtitle, icon: Icon, color = 'primary', percentage }) {
  const colorClasses = {
    primary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400',
    emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
  };
  
  return (
    <div className="card p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-surface-500 dark:text-surface-400 text-sm">{title}</p>
          <p className="text-3xl font-bold text-surface-900 dark:text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-surface-500 dark:text-surface-400 text-sm mt-1">{subtitle}</p>
          )}
          {percentage !== undefined && (
            <div className="mt-3">
              <div className="w-full bg-surface-200 dark:bg-surface-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    percentage > 80 ? 'bg-red-500' : percentage > 60 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>
              <p className="text-xs text-surface-500 mt-1">{percentage.toFixed(1)}% of limit used</p>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function TypeBreakdownCard({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-4 flex items-center gap-2">
          <ChartBarIcon className="w-5 h-5 text-primary-500" />
          Storage by Type
        </h3>
        <div className="h-48 flex items-center justify-center text-surface-500">
          No data available
        </div>
      </div>
    );
  }
  
  return (
    <div className="card p-6">
      <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-4 flex items-center gap-2">
        <ChartBarIcon className="w-5 h-5 text-primary-500" />
        Storage by Type
      </h3>
      <div className="space-y-4">
        {data.map((item, i) => {
          const Icon = TYPE_ICONS[item.category] || ServerStackIcon;
          const color = COLORS[item.category] || '#64748b';
          const maxBytes = Math.max(...data.map(d => d.bytes));
          const widthPercent = maxBytes > 0 ? (item.bytes / maxBytes) * 100 : 0;
          
          return (
            <div key={i} className="group">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${color}20` }}
                  >
                    <Icon className="w-4 h-4" style={{ color }} />
                  </div>
                  <div>
                    <p className="font-medium text-surface-900 dark:text-white">{item.category}</p>
                    <p className="text-xs text-surface-500">
                      {item.tableCount} table{item.tableCount !== 1 ? 's' : ''} • {item.rows.toLocaleString()} rows
                    </p>
                  </div>
                </div>
                <span className="font-semibold text-surface-900 dark:text-white">{item.sizeFormatted}</span>
              </div>
              <div className="w-full bg-surface-200 dark:bg-surface-700 rounded-full h-2 overflow-hidden">
                <div
                  className="h-2 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${widthPercent}%`,
                    backgroundColor: color
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function WarehouseStorage() {
  const { getCurrentCompanyId, getCurrentCompany } = useAuthStore();
  const companyId = getCurrentCompanyId();
  const currentCompany = getCurrentCompany();
  
  const { data: storageData, isLoading, error, refetch } = useQuery({
    queryKey: ['warehouseStorage', companyId],
    queryFn: () => apiHelpers.getWarehouseStorage(companyId),
    enabled: !!companyId // Only run query if we have a company ID
  });
  
  const storage = storageData?.data;
  
  // Show message if no company is selected (for super/managing admins)
  if (!companyId) {
    return (
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            Warehouse Storage
          </h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            Monitor your data warehouse space usage
          </p>
        </div>
        <div className="card p-8 text-center">
          <CircleStackIcon className="w-12 h-12 text-surface-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-2">
            No Company Selected
          </h3>
          <p className="text-surface-500 dark:text-surface-400">
            Please select a company from the sidebar to view warehouse storage details.
          </p>
        </div>
      </div>
    );
  }
  
  // Bar chart for storage by type
  const getTypeBarChartOptions = () => {
    if (!storage?.byType?.length) return null;
    
    const categories = storage.byType.map(d => d.category);
    const values = storage.byType.map(d => d.bytes / (1024 * 1024)); // Convert to MB
    const colors = storage.byType.map(d => COLORS[d.category] || '#64748b');
    
    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#1e293b',
        borderColor: '#334155',
        textStyle: { color: '#f1f5f9' },
        formatter: (params) => {
          const param = params[0];
          const item = storage.byType[param.dataIndex];
          return `
            <div style="font-weight: 600;">${param.name}</div>
            <div style="margin-top: 4px;">${item.sizeFormatted}</div>
            <div style="color: #94a3b8; font-size: 12px;">${item.tableCount} tables • ${item.rows.toLocaleString()} rows</div>
          `;
        }
      },
      grid: {
        top: 20,
        right: 20,
        bottom: 40,
        left: 100
      },
      xAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#e2e8f0' } },
        axisLabel: { 
          color: '#64748b',
          formatter: (value) => {
            if (value >= 1024) {
              return `${(value / 1024).toFixed(1)} GB`;
            }
            return `${value.toFixed(0)} MB`;
          }
        }
      },
      yAxis: {
        type: 'category',
        data: categories,
        axisLine: { lineStyle: { color: '#cbd5e1' } },
        axisLabel: { color: '#1e293b', fontWeight: 500 }
      },
      series: [{
        type: 'bar',
        data: values.map((value, i) => ({
          value,
          itemStyle: { 
            color: colors[i],
            borderRadius: [0, 4, 4, 0]
          }
        })),
        barWidth: '60%',
        label: {
          show: true,
          position: 'right',
          color: '#64748b',
          formatter: (params) => storage.byType[params.dataIndex].sizeFormatted
        }
      }]
    };
  };
  
  // Pie chart for storage distribution
  const getPieChartOptions = () => {
    if (!storage?.byType?.length) return null;
    
    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: '#1e293b',
        borderColor: '#334155',
        textStyle: { color: '#f1f5f9' },
        formatter: (params) => {
          return `
            <div style="font-weight: 600;">${params.name}</div>
            <div style="margin-top: 4px;">${params.data.sizeFormatted}</div>
            <div style="color: #94a3b8; font-size: 12px;">${params.percent.toFixed(1)}% of total</div>
          `;
        }
      },
      legend: {
        orient: 'vertical',
        right: 20,
        top: 'center',
        textStyle: { color: '#64748b' }
      },
      series: [{
        type: 'pie',
        radius: ['45%', '75%'],
        center: ['35%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 6,
          borderColor: '#ffffff',
          borderWidth: 2
        },
        label: {
          show: false
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold',
            color: '#1e293b'
          },
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.2)'
          }
        },
        data: storage.byType.map(item => ({
          value: item.bytes,
          name: item.category,
          sizeFormatted: item.sizeFormatted,
          itemStyle: {
            color: COLORS[item.category] || '#64748b'
          }
        }))
      }]
    };
  };
  
  // Data source bar chart
  const getDataSourceChartOptions = () => {
    if (!storage?.byDataSource?.length) return null;
    
    const data = storage.byDataSource.slice(0, 10); // Top 10
    
    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#1e293b',
        borderColor: '#334155',
        textStyle: { color: '#f1f5f9' },
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const param = params[0];
          const item = data[param.dataIndex];
          return `
            <div style="font-weight: 600;">${param.name}</div>
            <div style="margin-top: 4px;">${item.sizeFormatted}</div>
            <div style="color: #94a3b8; font-size: 12px;">${item.rows.toLocaleString()} rows</div>
          `;
        }
      },
      grid: {
        top: 20,
        right: 20,
        bottom: 60,
        left: 20
      },
      xAxis: {
        type: 'category',
        data: data.map(d => d.name),
        axisLine: { lineStyle: { color: '#cbd5e1' } },
        axisLabel: { 
          color: '#64748b',
          rotate: 30,
          interval: 0,
          fontSize: 11
        }
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#e2e8f0' } },
        axisLabel: { 
          color: '#64748b',
          formatter: (value) => {
            if (value >= 1024 * 1024 * 1024) {
              return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
            } else if (value >= 1024 * 1024) {
              return `${(value / (1024 * 1024)).toFixed(0)} MB`;
            } else if (value >= 1024) {
              return `${(value / 1024).toFixed(0)} KB`;
            }
            return `${value} B`;
          }
        }
      },
      series: [{
        type: 'bar',
        data: data.map(item => ({
          value: item.bytes,
          itemStyle: { 
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#3b82f6' },
                { offset: 1, color: '#1d4ed8' }
              ]
            },
            borderRadius: [4, 4, 0, 0]
          }
        })),
        barMaxWidth: 50
      }]
    };
  };
  
  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <div className="skeleton h-8 w-48 mb-2" />
        <div className="skeleton h-4 w-64 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card p-6">
              <div className="skeleton h-20" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6 h-80">
            <div className="skeleton h-full" />
          </div>
          <div className="card p-6 h-80">
            <div className="skeleton h-full" />
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            Warehouse Storage
          </h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            Monitor your data warehouse space usage
          </p>
        </div>
        <div className="card p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
            <div>
              <p className="text-red-700 dark:text-red-400 font-medium">Error loading storage data</p>
              <p className="text-red-600/70 dark:text-red-400/70 text-sm">
                {error.response?.data?.error || error.message}
              </p>
            </div>
          </div>
          <button onClick={() => refetch()} className="btn-secondary mt-4">
            <ArrowPathIcon className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  const typeChartOptions = getTypeBarChartOptions();
  const pieChartOptions = getPieChartOptions();
  const dataSourceChartOptions = getDataSourceChartOptions();
  
  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            Warehouse Storage
          </h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            {currentCompany?.name ? (
              <>Storage usage for <span className="font-medium text-surface-700 dark:text-surface-300">{currentCompany.name}</span></>
            ) : (
              'Monitor your data warehouse space usage'
            )}
          </p>
        </div>
        <button 
          onClick={() => refetch()}
          className="btn-secondary"
          disabled={isLoading}
        >
          <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StorageCard
          title="Total Storage Used"
          value={storage?.totalFormatted || '0 B'}
          subtitle={`Limit: ${storage?.limitFormatted || '10 GB'}`}
          icon={CircleStackIcon}
          color="primary"
          percentage={storage?.percentage}
        />
        <StorageCard
          title="Total Tables"
          value={storage?.tableCount || 0}
          subtitle="Across all types"
          icon={TableCellsIcon}
          color="emerald"
        />
        <StorageCard
          title="Total Rows"
          value={(storage?.totalRows || 0).toLocaleString()}
          subtitle="Across all tables"
          icon={ServerStackIcon}
          color="purple"
        />
        <StorageCard
          title="Data Types"
          value={storage?.byType?.length || 0}
          subtitle="Raw, Marts, Views, Staging"
          icon={CubeIcon}
          color="amber"
        />
      </div>
      
      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Type Breakdown Card */}
        <TypeBreakdownCard data={storage?.byType} />
        
        {/* Pie Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-4 flex items-center gap-2">
            <CircleStackIcon className="w-5 h-5 text-emerald-500" />
            Storage Distribution
          </h3>
          {pieChartOptions ? (
            <ReactECharts option={pieChartOptions} style={{ height: 280 }} />
          ) : (
            <div className="h-[280px] flex items-center justify-center text-surface-500">
              No data available
            </div>
          )}
        </div>
      </div>
      
      {/* Bar Chart - By Type */}
      <div className="card p-6 mb-6">
        <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-4 flex items-center gap-2">
          <ChartBarIcon className="w-5 h-5 text-blue-500" />
          Storage by Type
        </h3>
        {typeChartOptions ? (
          <ReactECharts option={typeChartOptions} style={{ height: 200 }} />
        ) : (
          <div className="h-[200px] flex items-center justify-center text-surface-500">
            No data available
          </div>
        )}
      </div>
      
      {/* Data Sources Chart */}
      {storage?.byDataSource?.length > 0 && (
        <div className="card p-6 mb-6">
          <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-4 flex items-center gap-2">
            <ServerStackIcon className="w-5 h-5 text-purple-500" />
            Top Data Sources by Size
          </h3>
          <ReactECharts option={dataSourceChartOptions} style={{ height: 300 }} />
        </div>
      )}
      
      {/* Tables List */}
      {storage?.tables?.length > 0 && (
        <div className="card">
          <div className="p-4 border-b border-surface-200 dark:border-surface-800">
            <h3 className="font-semibold text-surface-900 dark:text-white flex items-center gap-2">
              <TableCellsIcon className="w-5 h-5 text-amber-500" />
              All Tables
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200 dark:border-surface-700">
                  <th className="text-left text-sm font-medium text-surface-500 dark:text-surface-400 py-3 px-4">Table Name</th>
                  <th className="text-left text-sm font-medium text-surface-500 dark:text-surface-400 py-3 px-4">Type</th>
                  <th className="text-right text-sm font-medium text-surface-500 dark:text-surface-400 py-3 px-4">Rows</th>
                  <th className="text-right text-sm font-medium text-surface-500 dark:text-surface-400 py-3 px-4">Size</th>
                </tr>
              </thead>
              <tbody>
                {storage.tables.map((table, i) => {
                  const Icon = TYPE_ICONS[table.category] || ServerStackIcon;
                  const color = COLORS[table.category] || '#64748b';
                  
                  return (
                    <tr 
                      key={i} 
                      className="border-b border-surface-100 dark:border-surface-800 hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-surface-900 dark:text-white">{table.friendlyName}</p>
                          {table.friendlyName !== table.tableName && (
                            <p className="text-xs text-surface-500 font-mono">{table.tableName}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span 
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                          style={{ 
                            backgroundColor: `${color}15`,
                            color
                          }}
                        >
                          <Icon className="w-3 h-3" />
                          {table.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-surface-600 dark:text-surface-300">
                        {table.rows.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-surface-900 dark:text-white">
                        {table.sizeFormatted}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {storage.tableCount > 50 && (
            <div className="p-4 text-center text-surface-500 text-sm border-t border-surface-200 dark:border-surface-800">
              Showing top 50 of {storage.tableCount} tables
            </div>
          )}
        </div>
      )}
    </div>
  );
}
